import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "@mui/material";
import { BiLogInCircle } from "react-icons/bi";
import { FaWindows } from "react-icons/fa";
import packageJson from "@root/package.json";

// Redux Actions
import {
  getUserAccessToken,
} from "@redux/actions/utility-actions";

// Shared utilities
import { ROUTES } from "@shared/constants";
import { useNotification } from "@shared/hooks";

// Feature hooks
import { usePasswordReset, useSSOLogin } from "../../hooks";

// Shared components
import { CustomLoader, CustomButton, CustomInput, CustomCheckbox } from "@shared/components";

// Feature components
import { ResetPasswordModal, SuccessModal } from "./LoginModals";

const Login = () => {
  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem('mconnect_remember_me') === 'true'
  );
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Refs for uncontrolled inputs
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // Pre-fill remembered email
  useEffect(() => {
    const savedEmail = localStorage.getItem('mconnect_remembered_email');
    if (savedEmail && emailRef.current) {
      emailRef.current.value = savedEmail;
    }
  }, []);

  // Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showError } = useNotification();

  // Redux selectors with custom equality checks
  const isAuthenticated = useSelector(
    (state) => state?.tokens?.isAuthenticated,
    (prev, next) => prev === next
  );
  const accessToken = useSelector(
    (state) => state?.tokens?.accessToken,
    (prev, next) => prev === next
  );

  // Memoize app version
  const appVersion = useMemo(() => packageJson.version, []);

  // Custom hooks
  const passwordReset = usePasswordReset();
  const ssoLogin = useSSOLogin();

  // Memoized combined loading state
  const isLoading = useMemo(
    () => isSubmitting || passwordReset.loading || ssoLogin.loading,
    [isSubmitting, passwordReset.loading, ssoLogin.loading]
  );

  // Navigate after successful login (only once)
  useEffect(() => {
    if (loginSuccess && isAuthenticated && accessToken) {
      navigate(ROUTES.DASHBOARD);
      setLoginSuccess(false); // Reset flag
    }
  }, [loginSuccess, isAuthenticated, accessToken, navigate]);

  // Show error notifications - memoized callback
  const showPasswordResetError = useCallback(() => {
    if (passwordReset.error) {
      showError(passwordReset.error);
    }
  }, [passwordReset.error, showError]);

  const showSSOError = useCallback(() => {
    if (ssoLogin.error) {
      showError(ssoLogin.error);
    }
  }, [ssoLogin.error, showError]);

  useEffect(() => {
    showPasswordResetError();
  }, [showPasswordResetError]);

  useEffect(() => {
    showSSOError();
  }, [showSSOError]);

  // Memoized handlers for modals
  const handleShowResetModal = useCallback(() => {
    passwordReset.setShowModal(true);
  }, [passwordReset]);

  const handleCloseResetModal = useCallback(() => {
    passwordReset.setShowModal(false);
  }, [passwordReset]);

  const handleCloseSuccessModal = useCallback(() => {
    passwordReset.setIsResetSuccess(false);
  }, [passwordReset]);

  // Memoized form submission handler
  const handleLoginSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const email = emailRef.current?.value || "";
    const password = passwordRef.current?.value || "";
    
    // Basic validation
    const newErrors = { email: "", password: "" };
    if (!email) {
      newErrors.email = "Please enter the Email ID";
    }
    if (!password) {
      newErrors.password = "Please enter the Password";
    }
    
    setErrors(newErrors);
    
    if (newErrors.email || newErrors.password) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      const credentials = {
        username: email,
        password: password,
      };

      // getUserAccessToken already fetches all masters internally
      await dispatch(getUserAccessToken(credentials));
      
      // Persist Remember Me preference
      if (rememberMe) {
        localStorage.setItem('mconnect_remember_me', 'true');
        localStorage.setItem('mconnect_remembered_email', email);
      } else {
        localStorage.removeItem('mconnect_remember_me');
        localStorage.removeItem('mconnect_remembered_email');
      }

      // Trigger navigation effect after successful login
      setLoginSuccess(true);
      
      // Clear form only on success
      if (emailRef.current) emailRef.current.value = "";
      if (passwordRef.current) passwordRef.current.value = "";
      
    } catch (error) {
      showError(error.message || "Login failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, showError]);

  return (
    <>
      <CustomLoader loading={isLoading} />

      {/* Login Container */}
      <div className="container">
        <div className="row" style={{ justifyContent: "center" }}>
          <div className="col-lg-6 col-md-8 col-sm-10 col-12">
            <div className="my-16 mx-auto" style={{ maxWidth: '420px', padding: '0 1rem' }}>
              <div className="w-full mb-12">
                <h6 className="text-center text-[30px] font-bold text-[#3901FF] leading-[29px]">
                  LET'S GET STARTED
                </h6>
              </div>

              <div>
                <form onSubmit={handleLoginSubmit} className="w-full">
                  <div className="mb-0 !space-y-2">
                    {/* Email Input */}
                    <CustomInput
                      inputRef={emailRef}
                      type="email"
                      placeholder="Email ID"
                      name="email"
                      error={!!errors.email}
                      helperText={errors.email ? "Please enter the Email ID" : ""}
                      disableIcon={true}
                      onChange={useCallback(() => setErrors(prev => ({ ...prev, email: "" })), [])}
                      fullWidth
                      inputSXProps={{
                        '& .MuiInputBase-root': {
                          borderRadius: 0,
                        },
                        '& input': {
                          border: 0,
                          outline: 0,
                          height: '2.5rem',
                          paddingTop: '0.2rem',
                          paddingBottom: '0.2rem',
                        },
                        '& input::placeholder': {
                          fontSize: '15px',
                          fontWeight: 400,
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '10px',
                          color: '#e60000',
                          fontWeight: 500,
                          marginBottom: 0,
                        }
                      }}
                    />

                    {/* Password Input */}
                    <CustomInput
                      inputRef={passwordRef}
                      type="password"
                      placeholder="Password"
                      name="password"
                      error={!!errors.password}
                      helperText={errors.password ? "Please enter the Password" : ""}
                      disableIcon={true}
                      onChange={useCallback(() => setErrors(prev => ({ ...prev, password: "" })), [])}
                      fullWidth
                      inputSXProps={{
                        '& .MuiInputBase-root': {
                          borderRadius: 0,
                        },
                        '& input': {
                          border: 0,
                          outline: 0,
                          background: 'transparent',
                          height: '2.5rem',
                          paddingTop: '0.2rem',
                          paddingBottom: '0.2rem',
                        },
                        '& input::placeholder': {
                          fontSize: '15px',
                          fontWeight: 400,
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '10px',
                          color: '#e60000',
                          fontWeight: 500,
                          marginBottom: 0,
                        }
                      }}
                    />
                  </div>

                  {/* Remember Me & Forgot Password */}
                  <div className="flex justify-between items-center mt-2">
                    <CustomCheckbox
                      id="rememberMe"
                      label="Remember Me"
                      checked={rememberMe}
                      onChange={useCallback((e) => setRememberMe(e.target.checked), [])}
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 400,
                        marginLeft: '-10px',
                        '& .MuiFormControlLabel-label': {
                          color: '#5A5A5A',
                          fontSize: '0.7rem',
                          fontWeight: 400,
                          '@media (min-width: 768px)': {
                            fontSize: '0.9rem',
                          },
                        },
                        '& .MuiCheckbox-root': {
                          '&:focus': {
                            boxShadow: 'none !important',
                          },
                          '&:hover': {
                            boxShadow: 'none !important',
                          }
                        }
                      }}
                    />
                    <Link
                      component="button"
                      type="button"
                      onClick={handleShowResetModal}
                      underline="none"
                      sx={{
                        color: '#5A5A5A',
                        fontSize: '0.7rem',
                        fontWeight: 400,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#3901FF',
                        },
                        '@media (min-width: 768px)': {
                          fontSize: '0.9rem',
                        },
                      }}
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  {/* Login Button */}
                  <div className="text-center  mt-4">
                    <CustomButton
                      variant="primary"
                      type="submit"
                      disabled={isLoading}
                      className="!bg-[#3901FF] !rounded-lg !w-[200px] !h-[39px] !mb-2"
                    >
                      {isSubmitting ? "Logging in..." : "Login"}
                      <BiLogInCircle
                        style={{
                          marginLeft: "4px",
                          height: "1.1em",
                          width: "1.1em",
                        }}
                      />
                    </CustomButton>
                  </div>
                </form>

                {/* SSO Login */}
                <div className="text-center mt-0">
                  <CustomButton
                    variant="secondary"
                    onClick={ssoLogin.handleSSOLogin}
                    disabled={isLoading}
                    className="!bg-[#FF8C00] !border-[#FF8C00] !rounded-lg !w-[200px] !h-[39px] !mb-2 !mt-4"
                  >
                    Login with Outlook
                    <FaWindows
                      style={{
                        marginLeft: "4px",
                        height: "1.1em",
                        width: "1.1em",
                      }}
                    />
                  </CustomButton>
                </div>

                <div className="mt-[10%] text-center text-[#949494] text-base font-medium bg-transparent">
                  M-Connect v{appVersion}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        show={passwordReset.showModal}
        onClose={handleCloseResetModal}
        emailId={passwordReset.emailId}
        setEmailId={passwordReset.setEmailId}
        validationError={passwordReset.validationError}
        setValidationError={passwordReset.setValidationError}
        onReset={passwordReset.handleReset}
      />

      {/* Success Modal */}
      <SuccessModal
        show={passwordReset.isResetSuccess}
        onClose={handleCloseSuccessModal}
        emailId={passwordReset.emailId}
      />
    </>
  );
};

// Memoize component to prevent unnecessary re-renders
export default memo(Login);
